<template>
  <article class="faq-column">
    <h2 id="pricing-faq-heading">Frequently asked questions about payment and membership</h2>
    <accordion header-id="pricing-faq-heading">
      <accordion-item title="Which payment methods are accepted?">
        <p>
          We accept credit and debit cards directly at the charger, including Visa, Mastercard, American Express, and
          Discover. We also accept payments through the Electrify America mobile app, select automaker apps, Apple Pay,
          and Google Wallet.
        </p>
        <div class="tip">
          <img loading="lazy" src="../assets/images/WhatToExpect/icon-star.png" alt="" />
          <p>
            Electrify America will never ask you to complete payment by scanning a QR code. If you see a suspicious
            sticker or QR code on one of our chargers asking you to pay, please report it by selecting the station in
            the app and tapping “Support” then “Report Issue.”
          </p>
        </div>
      </accordion-item>
      <accordion-item title="How is pricing determined?">
        <p>
          Pricing for DC fast charging is determined by charger location, your plan, and the amount of energy delivered.
        </p>
        <p>
          At select stations, where time-of-use (TOU) pricing is implemented, the time of day may impact pricing as
          well. Select stations with TOU pricing offer lower charging rates during off-peak hours, so drivers can save
          money when charging demand is low. Off-peak hours and TOU pricing vary by station; check the Electrify America
          app or the charger’s screen for real-time pricing.
        </p>
        <p>
          For more details, including power levels and idle fees, check the charger screen or the Electrify America app.
        </p>
      </accordion-item>
      <accordion-item title="How do pricing levels work at different locations?">
        <p>
          Real-time pricing is available in the app or displayed on the charger before you begin charging. Pricing for
          DC fast charging is determined by charger location, your plan, and the amount of energy delivered. For more
          details, including power levels and idle fees, check the charger screen or the Electrify America app.
        </p>
      </accordion-item>
      <accordion-item title="How do I pay for charging and for the Pass+ subscription fee?">
        <p>
          First, sign up for Electrify America’s Pass or Pass+ membership in the mobile app. After signing up, follow
          the prompts to add a debit or credit card to your account. All costs for charging, idling, and any applicable
          taxes will be charged to the payment method saved to your account at the end of your charging session.
        </p>
        <p>
          To ensure sufficient funds are available, a temporary authorization hold in $20 increments will be placed on
          the card on file. You will only be charged for the actual cost of charging. Hold release times vary by
          bank<reference-link :num="1">
            You can manage your plan anytime in the mobile app under “Payment & Plans.” For longer charging sessions,
            additional holds in $20 increments may be placed as needed. You are responsible for all applicable fees
            incurred during a charging session. </reference-link
          >.
        </p>
        <p>
          If you’re a Pass+ member, your monthly subscription fee will be charged to your credit or debit card when you
          sign up and deducted monthly thereafter.
        </p>
        <p>Any remaining account balance will automatically be applied to your charging session.</p>
      </accordion-item>
      <accordion-item title="How do I change my membership plan?">
        <p>
          To change membership plans, visit “Plan details'' in the Electrify America app. Please note that a change from
          Pass+ to Pass, will not be reflected until the next billing cycle. Because of this, your Electrify America
          Pass+ plan will remain active until the end of the current billing cycle.
        </p>
      </accordion-item>
      <accordion-item title="What happens if my Electrify America payment is invalid?">
        <p>
          If your payment method declines during a charging session, please update it to continue charging your vehicle.
          If the payment method is invalid, charging will not proceed.
        </p>
        <p>
          You can
          <a class="link" href="https://www.youtube.com/watch?v=AqKKD66TFJ0" target="_blank">update your credit card</a>
          in the Electrify America app under <strong>Account>Payments>Add a Payment Method</strong>.
        </p>
      </accordion-item>
      <accordion-item title="How do I switch from one membership type to another?">
        <p>In the app, go to “Plan details” and follow the prompts to upgrade to Pass+ or to change plan types.</p>
        <p>
          Please note that a request to change from Pass+ to Pass membership will go into effect on the first day of the
          next billing cycle, which coincides with the monthly anniversary of your sign-up date.
        </p>
      </accordion-item>
      <accordion-item title="How do I delete my account?">
        <p>Cancel your membership anytime in the app by navigating to your profile and selecting “Delete Account.”</p>
      </accordion-item>
      <accordion-item title="Why does EV charging pricing vary by location?">
        <p>
          Real-time pricing is available in the app or displayed on the charger before you begin charging. Pricing for
          DC fast charging is determined by the charger location, your plan, and the amount of energy delivered.
        </p>
      </accordion-item>
      <accordion-item title="How can I find out the current pricing at my favorite charging location?">
        <p>
          Real-time pricing is available in the app or displayed on the charger before you begin charging. Pricing for
          DC fast charging is determined by the charger location, your plan, and the amount of energy delivered.
        </p>
      </accordion-item>
      <accordion-item title="Does this new pricing change my Premium Offer plan?">
        <p>
          Premium Offer plans that include complimentary charging aren’t affected by these changes. Once your plan
          expires or you exceed your plan benefits for a session, the remainder of your charging will automatically be
          billed at our Pass plan pricing. You can choose to enroll in Pass+ for additional savings.
        </p>
      </accordion-item>
      <accordion-item title="What is the cost to charge in Canada?">
        <p>
          For more information on Canada Charging, please visit
          <a class="link" href="https://www.electrify-canada.ca/" rel="noopener" target="_blank">Electrify Canada</a> or
          for Canadian Pricing, please visit
          <a class="link" href="https://www.electrify-canada.ca/pricing/" rel="noopener" target="_blank"
            >Electrify Canada Pricing & Plans</a
          >
          .
        </p>
      </accordion-item>
    </accordion>
  </article>
</template>

<script>
import Accordion from './Accordion/Accordion.vue';
import AccordionItem from './Accordion/AccordionItem.vue';
import ReferenceLink from './ReferenceLink/ReferenceLink.vue';

export default {
  name: 'faqs',
  components: {
    Accordion,
    AccordionItem,
    ReferenceLink,
  },
};
</script>

<style scoped lang="scss">
.accordion-with-image {
  display: flex;
  flex-direction: column;
  margin-bottom: 48px;
  img {
    max-width: 225px;
    height: auto;
  }
  @media (min-width: 750px) {
    flex-direction: row;
  }
}
.tip {
  display: flex;
  flex-direction: row;
  margin-top: 10px;
  margin-bottom: 10px;
  img {
    width: 20px !important;
    height: 20px;
    margin: 0;
    margin-top: 2px;
  }
  p {
    margin: 0;
    margin-left: 5px;
  }
}
</style>
